import { openAddressForm } from './address-form.js';

// Custom Loqate autocomplete. Renders its own dropdown inside the dialog so
// the suggestion list sits in the same top-layer context as the modal — no
// need for the pca.Address JS library or its CSS.

const FIND_URL     = 'https://api.addressy.com/Capture/Interactive/Find/v1.10/json3.ws';
const RETRIEVE_URL = 'https://api.addressy.com/Capture/Interactive/Retrieve/v1.20/json3.ws';

function loqateKey() {
    return document.querySelector('meta[name="qlazsrisbm"]')?.content || null;
}

// The country widget's hidden <input name="country"> carries the
// ISO 3166-1 alpha-2 code (the same string Loqate accepts in its
// Countries query parameter). Used by every Loqate Find call so the
// search hits the country the customer picked.
//
// Resolution order:
//  1. If the search input is inside a <form> that has a country control,
//     use that (address-form dialogs go this way).
//  2. Otherwise walk up looking for an ancestor that contains a country
//     widget — the checkout's inline billing search sits outside any
//     <form> and shares a section with its own widget.
function loqateCountryFor(searchInput) {
    const fromForm = searchInput?.closest('form')?.elements?.country;
    if (fromForm?.value) return fromForm.value;
    let el = searchInput?.parentElement;
    while (el) {
        const country = el.querySelector(':scope .addr-country input[name="country"]');
        if (country?.value) return country.value;
        el = el.parentElement;
    }
    return 'GB';
}

// Hit Loqate's Find endpoint filtered to a specific postcode and report
// whether at least one Address-type item comes back. Used by the
// address-form submit validator. Returns true when the postcode resolves,
// false when it doesn't, and null when the call can't be made (no key,
// network error) — callers treat null as "skip the check".
export async function postcodeResolves(value) {
    const key = loqateKey();
    if (!key || !value) return null;
    const params = new URLSearchParams({
        Key:       key,
        Text:      value,
        Filters:   `Postcode:${value}`,
        Countries: 'GBR',
        Limit:     '10',
    });
    try {
        const res  = await fetch(`${FIND_URL}?${params}`);
        const data = await res.json();
        return (data.Items || []).some(item => item.Type === 'Address');
    } catch (err) {
        console.error('Postcode validation failed', err);
        return null;
    }
}

// Loqate "Retrieve" field name → our form input name
const FIELD_MAP = {
    addressLine1: 'Line1',
    addressLine2: 'Line2',
    addressLine3: 'Line3',
    city:         'City',
    postcode:     'PostalCode',
};

export function initAddressLookup() {
    const key = loqateKey();
    if (!key) return;

    let idx = 0;

    // Inline searches that populate the surrounding <form> (e.g. the delivery
    // dialog's own search field).
    document.querySelectorAll('[data-address-capture]').forEach(input => {
        wireAutocomplete(input, key, idx++);
    });

    // Inline searches that open the address-form popup with the picked
    // address pre-filled — used on the checkout page's billing-address field.
    document.querySelectorAll('[data-address-popup-trigger]').forEach(input => {
        wireBillingAutocomplete(input, key, idx++);
    });
}

function wireAutocomplete(searchInput, key, index, { onSelect } = {}) {
    const form    = searchInput.closest('form');
    const wrapper = searchInput.closest('.form-field');
    if (!wrapper) return;
    if (!form && !onSelect) return;

    const dropdown = document.createElement('ul');
    dropdown.className = 'addr-suggestions';
    dropdown.id        = `addr-suggestions-${index}`;
    dropdown.hidden    = true;
    dropdown.setAttribute('role', 'listbox');
    wrapper.appendChild(dropdown);

    searchInput.setAttribute('role',              'combobox');
    searchInput.setAttribute('aria-controls',     dropdown.id);
    searchInput.setAttribute('aria-expanded',     'false');
    searchInput.setAttribute('aria-autocomplete', 'list');

    let container    = '';
    let activeIndex  = -1;
    let debounce     = null;
    let inFlight     = null;

    function show() {
        dropdown.hidden = false;
        searchInput.setAttribute('aria-expanded', 'true');
    }
    function hide() {
        dropdown.hidden = true;
        searchInput.setAttribute('aria-expanded', 'false');
        searchInput.removeAttribute('aria-activedescendant');
        activeIndex = -1;
    }

    searchInput.addEventListener('input', () => {
        clearTimeout(debounce);
        const text = searchInput.value.trim();
        if (!text) {
            container = '';
            hide();
            return;
        }
        debounce = setTimeout(() => find(text, container), 250);
    });

    searchInput.addEventListener('keydown', e => {
        if (e.key === 'Escape' && !dropdown.hidden) {
            e.preventDefault();
            e.stopPropagation();
            hide();
            return;
        }
        if (dropdown.hidden) return;
        const items = dropdown.children;
        if (!items.length) return;

        if (e.key === 'ArrowDown') {
            e.preventDefault();
            activeIndex = Math.min(activeIndex + 1, items.length - 1);
            updateActive();
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            activeIndex = Math.max(activeIndex - 1, 0);
            updateActive();
        } else if (e.key === 'Enter' && activeIndex >= 0) {
            e.preventDefault();
            items[activeIndex].click();
        }
    });

    document.addEventListener('click', e => {
        if (!wrapper.contains(e.target)) hide();
    });

    async function find(text, containerId) {
        if (inFlight) inFlight.abort();
        inFlight = new AbortController();

        const params = new URLSearchParams({
            Key:       key,
            Text:      text,
            Countries: loqateCountryFor(searchInput),
            Limit:     '8',
        });
        if (containerId) params.set('Container', containerId);

        try {
            const res  = await fetch(`${FIND_URL}?${params}`, { signal: inFlight.signal });
            const data = await res.json();
            const items = data.Items || [];
            render(items.length === 1 && items[0].Error ? [] : items);
        } catch (err) {
            if (err.name !== 'AbortError') console.error('Address find failed', err);
        }
    }

    function render(items) {
        dropdown.replaceChildren();
        activeIndex = -1;

        items.forEach((item, i) => {
            const li = document.createElement('li');
            li.className = 'addr-suggestions__item';
            li.id        = `${dropdown.id}-item-${i}`;
            li.setAttribute('role', 'option');

            const text = document.createElement('span');
            text.className = 'addr-suggestions__text';
            text.innerHTML = formatHighlight(item.Text, item.Highlight);
            li.appendChild(text);

            if (item.Description) {
                const desc = document.createElement('span');
                desc.className   = 'addr-suggestions__desc';
                desc.textContent = item.Description;
                li.appendChild(desc);
            }

            li.addEventListener('mousedown', e => e.preventDefault());
            li.addEventListener('click', () => pick(item));
            dropdown.appendChild(li);
        });

        appendManualEntry();
        show();
    }

    function appendManualEntry() {
        const li = document.createElement('li');
        li.className = 'addr-suggestions__item addr-suggestions__item--manual';
        li.id        = `${dropdown.id}-item-manual`;
        li.setAttribute('role', 'option');
        li.textContent = 'Enter address manually';
        li.addEventListener('mousedown', e => e.preventDefault());
        li.addEventListener('click', manualEntry);
        dropdown.appendChild(li);
    }

    function manualEntry() {
        if (form) {
            Object.keys(FIELD_MAP).forEach(name => {
                const el = form.elements[name];
                if (!el) return;
                el.value = '';
                el.dispatchEvent(new Event('input', { bubbles: true }));
            });
        } else if (onSelect) {
            // Billing-style search has no inline form — open the address-form
            // popup with blank address lines, same as picking a real address.
            onSelect({});
        }
        searchInput.value = '';
        container = '';
        hide();
        if (form) form.elements.addressLine1?.focus();
    }

    function updateActive() {
        Array.from(dropdown.children).forEach((el, i) => {
            const isActive = i === activeIndex;
            el.classList.toggle('is-active', isActive);
            if (isActive) {
                el.scrollIntoView({ block: 'nearest' });
                searchInput.setAttribute('aria-activedescendant', el.id);
            }
        });
    }

    async function pick(item) {
        if (item.Type === 'Address') {
            const address = await retrieve(item.Id);
            if (address) {
                if (onSelect) onSelect(address);
                else populateForm(address);
            }
            searchInput.value = '';
            container = '';
            hide();
        } else {
            container = item.Id;
            await find(searchInput.value.trim(), container);
        }
    }

    async function retrieve(id) {
        const params = new URLSearchParams({ Key: key, Id: id });
        try {
            const res  = await fetch(`${RETRIEVE_URL}?${params}`);
            const data = await res.json();
            const items = data.Items || [];
            if (items.length && !items[0].Error) return items[0];
        } catch (err) {
            console.error('Address retrieve failed', err);
        }
        return null;
    }

    function populateForm(address) {
        if (!form) return;
        Object.entries(FIELD_MAP).forEach(([formName, loqateField]) => {
            const el = form.elements[formName];
            if (!el) return;
            el.value = (address[loqateField] || '').trim();
            el.dispatchEvent(new Event('input', { bubbles: true }));
        });
    }
}

// Billing-style autocomplete: same Loqate UI, but on Address pick we open the
// address-form popup pre-filled with the picked address plus the page's own
// First Name / Last Name (so the user can review/edit before saving).
function wireBillingAutocomplete(searchInput, key, index) {
    const dialogId = searchInput.dataset.addressPopupTrigger;
    if (!dialogId) return;

    const section        = searchInput.closest('section') || document.body;
    const firstNameInput = section.querySelector('input[name="firstName"]');
    const lastNameInput  = section.querySelector('input[name="lastName"]');
    const countryInput   = section.querySelector('.addr-country input[name="country"]');

    wireAutocomplete(searchInput, key, index, {
        onSelect(address) {
            const prefill = {
                firstName:    firstNameInput?.value.trim() || '',
                lastName:     lastNameInput?.value.trim()  || '',
                addressLine1: (address.Line1 || '').trim(),
                addressLine2: (address.Line2 || '').trim(),
                addressLine3: (address.Line3 || '').trim(),
                city:         (address.City  || '').trim(),
                postcode:     (address.PostalCode || '').trim(),
                country:      countryInput?.value || '',
            };
            openAddressForm(dialogId, prefill, searchInput);
        },
    });
}

// ── Highlight runs returned by Loqate's Find response ───────────────────────
function formatHighlight(text, highlight) {
    if (!highlight) return escapeHtml(text);
    const ranges = highlight
        .split(';')
        .map(r => r.split(',').map(Number))
        .filter(([s, e]) => !isNaN(s) && !isNaN(e))
        .sort((a, b) => a[0] - b[0]);

    let out = '';
    let pos = 0;
    for (const [start, end] of ranges) {
        if (start > pos) out += escapeHtml(text.slice(pos, start));
        out += '<b>' + escapeHtml(text.slice(start, end)) + '</b>';
        pos = end;
    }
    if (pos < text.length) out += escapeHtml(text.slice(pos));
    return out;
}

function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, c => ({
        '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
    }[c]));
}

